// branding.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Branding } from 'src/app/models/tenant.model';
import { LoggingService } from '../consoleLogs';
@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  private configKey = 'tenantUI';
  // Cache parsed branding to avoid repeated JSON.parse on reload.
  // We cache based on the exact localStorage string value.
  private cachedTenantUIString: string | null = null;
  private cachedBranding: Branding | null = null;
  private hasCachedBranding: boolean = false;

  constructor(
    private meta: Meta,
    private titleService: Title,
    private loggingService : LoggingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  getBrandingConfig(): Branding | null {
    if (isPlatformBrowser(this.platformId)) {
      const cachedData = localStorage.getItem(this.configKey);

      // Reuse cached parse result if localStorage didn't change.
      if (cachedData === this.cachedTenantUIString && this.hasCachedBranding) {
        return this.cachedBranding;
      }

      // Update cache key even if cachedData is null.
      this.cachedTenantUIString = cachedData;
      this.hasCachedBranding = true;

      if (!cachedData) {
        this.cachedBranding = null;
        return null;
      }

      try {
        const tenantUI = JSON.parse(cachedData);
        this.cachedBranding = tenantUI?.data?.portalConfig?.branding ?? null;
        return this.cachedBranding;
      } catch (e) {
        this.loggingService.error('Error parsing branding config', e);
        this.cachedBranding = null;
        this.hasCachedBranding = true;
        return null;
      }
    }

    return null;
  }

  setFavicon(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    let faviconUrl = 'assets/default-favicon.ico';
    const brandingConfig = this.getBrandingConfig();

    if (brandingConfig?.logo?.url) {
      faviconUrl = brandingConfig.logo.url;
    }
    if (brandingConfig) {
      this.applyBranding(brandingConfig);
    }

    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = faviconUrl;
    document.head.appendChild(link);
  }

  setMetaTags(): void {
    const brandingConfig = this.getBrandingConfig();
    const defaultTitle = 'Medical Learning Platform';
    const defaultDescription = 'LMS is an innovative learning platform designed for healthcare professionals, providing access to interactive medical courses, certification programs, and continuous education.';
    this.titleService.setTitle(defaultTitle);
    this.updateMetaTag('description', defaultDescription);
    this.updateMetaTag('og:title', defaultTitle);
    this.updateMetaTag('og:description', defaultDescription);
    this.updateMetaTag('og:url', isPlatformBrowser(this.platformId) ? window.location.href : '');
    if (brandingConfig) {
      this.updateMetaTag('theme-color', brandingConfig.brandColor);
      this.updateMetaTag('msapplication-TileColor', brandingConfig.brandColor);
    }
  }

  private updateMetaTag(name: string, content: string): void {
    if (this.meta.getTag(`name='${name}'`)) {
      this.meta.updateTag({ name, content });
    } else if (this.meta.getTag(`property='${name}'`)) {
      this.meta.updateTag({ property: name, content });
    } else {
      if (name.startsWith('og:')) {
        this.meta.addTag({ property: name, content });
      } else {
        this.meta.addTag({ name, content });
      }
    }
  }

  getBrandingValue(key: string, fallback: string): string {
    const brandingConfig = this.getBrandingConfig();
    const value = (brandingConfig as any)?.[key];
    return typeof value === 'string' ? value : fallback;
  }

  // instead of separate functions
  getBrandColor(): string {
    return this.getBrandingValue('brandColor', '#1e73be');
  }

  getSecondaryColor(): string {
    return this.getBrandingValue('secondaryColor', '#1e73be');
  }

  getLaunchButton(): string {
    return this.getBrandingValue('launchButton', '#1e73be');
  }

  getPrimaryButton(): string {
    return this.getBrandingValue('primaryButton', '#1e73be');
  }

  getLaunchButtonhover(): string {
    return this.getBrandingValue('launchbuttonhover', '#1e73be');
  }

  getMainText(): string {
    return this.getBrandingValue('mainText', '#1e73be');
  }
  getSecButton(): string {
    return this.getBrandingValue('secButton', '#1e73be');

  }
  private lightenColor(hex: string, percent: number): string {
    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    r = Math.min(255, Math.floor(r + (255 - r) * percent / 100));
    g = Math.min(255, Math.floor(g + (255 - g) * percent / 100));
    b = Math.min(255, Math.floor(b + (255 - b) * percent / 100));

    return `rgb(${r}, ${g}, ${b})`;
  }

applyBranding(branding: Branding): void {
  if (!branding) return;

  // Lighten brand color by 20% (you can tweak % as needed)
  const lightPrimary = this.lightenColor(branding.brandColor, 80); //brandColor

  // Update CSS variables at runtime
  document.documentElement.style.setProperty('--light-primary', lightPrimary);
    document.documentElement.style.setProperty('--brand-color', branding.brandColor);

  document.documentElement.style.setProperty('--secondary-color', branding.secondaryColor);
  document.documentElement.style.setProperty('--primary-button', branding.primaryButton);
  document.documentElement.style.setProperty('--launch-button', branding.launchButton);
  document.documentElement.style.setProperty('--main-text', branding.mainText);
  document.documentElement.style.setProperty('--sec-button', branding.secButton);
}


}